<script setup lang="ts">
import { cva } from 'class-variance-authority';
import { computed, onMounted } from 'vue';
import { cn } from '@/helpers/tailwind-helpers';
import BaseLabel from '@/atoms/BaseLabel.vue';

export type BaseRadioButtonProps = {
  value: unknown;
  initialChecked?: boolean;
  class?: string;
  size?: 'medium';
  colorMode?: 'default' | 'on-brand';
  state?: 'default' | 'disabled' | 'hover';
  title?: string;
  description?: string;
};

const baseCheckbox = cva('flex rounded-full w-5 h-5 bg-white relative mt-0.5', {
  variants: {
    state: {
      default: 'border border-primary',
      hover: 'border border-primary bg-grey-25',
      disabled: 'border border-primary text-disabled-subtle',
    },
    checked: {
      true: '',
      false: '',
    },
    colorMode: {
      default: '',
      'on-brand': '',
    },
    size: {
      medium: '',
    },
  },
  compoundVariants: [
    {
      checked: true,
      colorMode: 'default',
      state: 'default',
      class: 'bg-brand-solid text-white border-0',
    },
    {
      checked: true,
      colorMode: 'default',
      state: 'hover',
      class: 'bg-blue-900 text-white border-0',
    },
    {
      checked: true,
      colorMode: 'on-brand',
      state: 'default',
      class: 'bg-white border-0 text-brand-solid',
    },
    {
      checked: true,
      colorMode: 'on-brand',
      state: 'hover',
      class: 'bg-grey-25 border-0 text-brand-solid rounded-xl',
    },
  ],
  defaultVariants: {
    state: 'default',
    size: 'medium',
    colorMode: 'default',
  },
});

const props = defineProps<BaseRadioButtonProps>();
const selectedValue = defineModel<unknown>();

onMounted(() => {
  if (props.initialChecked) {
    selectedValue.value = props.value;
  }
});

const classes = computed(() =>
  cn(
    baseCheckbox({
      ...props,
      checked: selectedValue.value === props.value,
    }),
    props.class
  ),
);
</script>

<template>
  <div class="flex space-x-2">
    <div :class="classes">
      <input
        :disabled="state === 'disabled'"
        type="radio"
        class="appearance-none w-full h-full z-10"
        :value="props.value"
        v-model="selectedValue"
      />
      <div v-if="selectedValue === value" class="absolute inset-0 flex items-center justify-center">
        <div class="w-2 h-2 bg-current rounded-full" />
      </div>
    </div>
    <div class="flex flex-col">
      <BaseLabel v-if="title" :text="title" preset="medium" />
      <BaseLabel v-if="description" :text="description" preset="small" color="secondary" />
    </div>
  </div>
</template>
